import { Component, computed, ElementRef, inject, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { AppMenuitemComponent } from './app.menuitem';
import { AuthService } from '@auth/services/auth.service';
import { PermissionsService } from '@auth/services/permissions.service';
import { ModuleService, ModuleType } from '@layout/service/module.service';
import { UserType } from 'api';
import {
  PERMISSION_MODULES,
  PERMISSIONS_COMPRAS,
  PERMISSIONS_CONTABILIDAD,
  PERMISSIONS_FABRICACION,
  PERMISSIONS_CONFIGURACIONES,
  PERMISSIONS_CRM,
  PERMISSIONS_FINANZAS,
  PERMISSIONS_GASTOS,
  PERMISSIONS_INVENTARIO,
  PERMISSIONS_E_COMMERCE,
  PERMISSIONS_GESTION_DE_RESELLERS,
  PERMISSIONS_NOMINA,
  PERMISSIONS_RECURSOS_HUMANOS,
  PERMISSIONS_SOPORTE,
  PERMISSIONS_PROYECTOS_Y_TAREAS,
  PERMISSIONS_TRIBUTARIO,
  PERMISSIONS_VENTAS,
  PERMISSIONS_PUNTO_DE_VENTA,
} from '@auth/constants';

export interface MenuItem {
  label?: string;
  icon?: string;
  routerLink?: string[];
  queryParams?: Record<string, unknown>;
  queryParamsHandling?: 'merge' | 'preserve' | 'replace' | '';
  fragment?: string;
  url?: string;
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
  routerLinkActiveOptions?: Record<string, unknown>;
  items?: MenuItem[];
  separator?: boolean;
  visible?: boolean;
  disabled?: boolean;
  command?: (event: import('primeng/api').MenuItemCommandEvent) => void;
  class?: string;
  style?: Record<string, unknown> | null;
  styleClass?: string;
  id?: string;
  urlTarget?: '_blank' | '_self' | '_parent' | '_top';
  moduleType?: ModuleType; // Nuevo campo para filtrar por módulo

  // Permisos
  /** ID del módulo de permisos del backend (ej: 'erp-ventas', 'erp-inventario') */
  permissionModuleId?: string;
  /** ID de permiso específico requerido para ver este ítem (ej: 'ventas-facturas-ver') */
  permissionId?: string;
  /** IDs alternativos: basta con tener uno para ver el ítem. */
  permissionIdsAny?: string[];
  /** Visible únicamente para usuarios asociados a un reseller. */
  resellerOnly?: boolean;
  /**
   * IDs acumulativos: hay que tenerlos **todos** para ver el ítem. Para pantallas cuya ruta gatea
   * con un permiso propio pero cuyo endpoint exige además otro (caso de los reportes de antigüedad
   * y estado de cuenta, que desde 2026-08-05 piden `finanzas-cuentas-cobrar-pagar-ver`): sin esto
   * el ítem se ve, se entra y el backend devuelve 403.
   */
  permissionIdsAll?: string[];
  /**
   * El ítem muestra **agregados de todo el tenant**: se oculta a los perfiles cuyo alcance de datos
   * está recortado (hoy `UserType.Seller`, al que el backend le devuelve solo sus propias filas en
   * los listados pero le entregaría el agregado completo). Va de la mano de `fullTenantScopeGuard`
   * en la ruta.
   *
   * ⚠️ **Sin ningún ítem que lo use desde 2026-08-05**: su único consumidor era el «Resumen» de
   * CxC/CxP, y el backend pasó a acotar `/dashboard` por el `sellerId` del JWT. Se conserva —con su
   * comprobación en `filterChildren` y su spec— porque es infraestructura genérica ya probada: en
   * cuanto aparezca otro endpoint agregado sin acotar, se vuelve a poner en su ítem de menú.
   */
  requiresFullTenantScope?: boolean;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menu]',
  standalone: true,
  imports: [AppMenuitemComponent, RouterModule],
  template: `<ul class="layout-menu" #menuContainer>
    @for (item of filteredModel(); track $index; let i = $index) {
      @if (!item.separator) {
        <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
      }
      @if (item.separator) {
        <li class="menu-separator"></li>
      }
    }
  </ul>`,
  host: {
    class: 'layout-menu-container',
  },
})
export class AppMenuComponent {
  private authService = inject(AuthService);
  private moduleService = inject(ModuleService);
  private permissionsService = inject(PermissionsService);

  isAdmin = computed(() => this.authService.isAdmin());

  el: ElementRef = inject(ElementRef);

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  // Computed que filtra el menú según el módulo seleccionado y los permisos del usuario
  filteredModel = computed(() => {
    const selectedModule = this.moduleService.selectedModule();

    const filterChildren = (items?: MenuItem[]): MenuItem[] => {
      if (!items) return [];

      const mapped = items.map((child) => {
        // Respetar visible=false explícito
        if (child.visible === false) return null;
        if (child.resellerOnly && !this.authService.user()?.resellerId) return null;

        // Verificar acceso a módulo si se especifica
        if (child.permissionModuleId && !this.permissionsService.hasModuleAccess(child.permissionModuleId)) {
          return null;
        }

        // Verificar permiso específico si se especifica
        if (child.permissionId && !this.permissionsService.hasPermission(child.permissionId)) {
          return null;
        }

        if (
          child.permissionIdsAny?.length &&
          !child.permissionIdsAny.some((id) => this.permissionsService.hasPermission(id))
        ) {
          return null;
        }

        // Permisos acumulativos: hay que tenerlos todos (ver `permissionIdsAll` en `MenuItem`).
        if (
          child.permissionIdsAll?.length &&
          !child.permissionIdsAll.every((id) => this.permissionsService.hasPermission(id))
        ) {
          return null;
        }

        // Pantallas de agregados de tenant: fuera para perfiles de alcance recortado.
        if (child.requiresFullTenantScope && this.permissionsService.userType() === UserType.Seller) {
          return null;
        }

        // Recursión: si el hijo tiene sub-ítems, filtrarlos también
        if (child.items) {
          const nestedFiltered = filterChildren(child.items);
          if (nestedFiltered.filter((c) => !c.separator).length === 0) return null;
          return { ...child, items: nestedFiltered };
        }

        return child;
      });

      return mapped.filter((child) => child !== null) as MenuItem[];
    };

    return this.fullMenuModel
      .filter((item: MenuItem) => {
        // Si es separador, no mostrarlo aquí (los separadores internos se mantienen en items)
        if (item.separator) return false;

        // Si el item tiene moduleType definido, debe coincidir con el seleccionado
        if (item.moduleType) {
          return item.moduleType === selectedModule;
        }

        // Si no tiene moduleType (por compatibilidad), no mostrarlo
        return false;
      })
      .map((item) => {
        // Respetar visible=false explícito
        if (item.visible === false) return null;

        // Verificar acceso al módulo si se especifica
        if (item.permissionModuleId && !this.permissionsService.hasModuleAccess(item.permissionModuleId)) {
          return null;
        }

        const filteredChildren = filterChildren(item.items);

        // Si el item tiene hijos y ninguno es visible (excluir separadores), ocultar el grupo
        if (item.items && filteredChildren.filter((c) => !c.separator).length === 0) {
          return null;
        }

        return {
          ...item,
          items: filteredChildren,
        } as MenuItem;
      })
      .filter((item) => item !== null) as MenuItem[];
  });

  fullMenuModel: MenuItem[] = buildNavigationModel(this.isAdmin());
}

export function buildNavigationModel(isAdmin: boolean): MenuItem[] {
  return [
    // ========================================
    // PANEL PRINCIPAL
    // ========================================
    {
      label: 'Panel Principal',
      icon: 'pi pi-home',
      moduleType: 'dashboard',
      items: [
        {
          label: 'Tablero Principal',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/dashboard'],
        },
        {
          label: 'Portal reseller',
          icon: 'pi pi-fw pi-briefcase',
          routerLink: ['/reseller'],
          permissionId: PERMISSIONS_GESTION_DE_RESELLERS.VER_DASHBOARD_RESELLER,
          resellerOnly: true,
        },
      ],
    },
    // ========================================
    // ACCESOS RÁPIDOS
    // ========================================
    {
      label: 'Accesos Rápidos',
      icon: 'pi pi-star',
      moduleType: 'dashboard',
      items: [
        {
          label: 'Nueva Factura',
          icon: 'pi pi-fw pi-file-plus',
          routerLink: ['/sales/invoices/quick-access'],
          permissionId: PERMISSIONS_VENTAS.CREAR_FACTURAS,
        },
        {
          label: 'Nueva Orden de venta',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/sales/orders/quick-access'],
          permissionId: PERMISSIONS_VENTAS.CREAR_RDENES_DE_VENTA,
        },
        { separator: true },
        { separator: true },
        { separator: true },
        {
          label: 'Nueva Nota de Entrega',
          icon: 'pi pi-fw pi-file-plus',
          routerLink: ['/sales/delivery-notes/quick-access'],
          permissionId: PERMISSIONS_VENTAS.CREAR_NOTAS_DE_ENTREGA,
        },
        {
          label: 'Nueva Nota de Devolución',
          icon: 'pi pi-fw pi-undo',
          routerLink: ['/sales/return-notes/quick-access'],
          permissionId: PERMISSIONS_VENTAS.CREAR_NOTAS_DE_DEVOLUCION,
        },
        {
          label: 'Nueva Cotización de Entrega',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/sales/delivery-quotations/quick-access'],
          permissionId: PERMISSIONS_VENTAS.CREAR_COTIZACIONES_DE_ENTREGA,
        },
        { separator: true },
        { separator: true },
        { separator: true },
        {
          label: 'Consultar Inventario',
          icon: 'pi pi-fw pi-search',
          routerLink: ['/inventory/movements/quick-access-balances'],
          permissionId: PERMISSIONS_INVENTARIO.VER_BALANCES,
        },
        {
          label: 'Ver Clientes',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/crm/customers/quick-access'],
          permissionId: PERMISSIONS_CRM.VER_CLIENTES,
        },
      ],
    },

    // ========================================
    // VENTAS
    // ========================================
    {
      label: 'Facturación',
      icon: 'pi pi-shopping-cart',
      moduleType: 'sales',
      permissionModuleId: PERMISSION_MODULES.VENTAS,
      items: [
        {
          label: 'Facturas',
          icon: 'pi pi-fw pi-file-check',
          routerLink: ['/sales/invoices'],
          permissionId: PERMISSIONS_VENTAS.VER_FACTURAS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Órdenes de venta',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/sales/orders'],
          permissionId: PERMISSIONS_VENTAS.VER_RDENES_DE_VENTA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Plantillas de Órdenes de venta',
          icon: 'pi pi-fw pi-copy',
          routerLink: ['/sales/quotations-templates'],
          permissionId: PERMISSIONS_VENTAS.VER_PLANTILLAS_DE_ORDEN_DE_VENTA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Notas de Entregas',
      icon: 'pi pi-shopping-cart',
      moduleType: 'sales',
      permissionModuleId: PERMISSION_MODULES.VENTAS,
      items: [
        {
          label: 'Notas de Entrega',
          icon: 'pi pi-fw pi-file-check',
          routerLink: ['/sales/delivery-notes'],
          permissionId: PERMISSIONS_VENTAS.VER_NOTAS_DE_ENTREGA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },

        {
          label: 'Notas de Devolución',
          icon: 'pi pi-fw pi-undo',
          routerLink: ['/sales/return-notes'],
          permissionId: PERMISSIONS_VENTAS.VER_NOTAS_DE_DEVOLUCION,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Cotizaciones de Entrega',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/sales/delivery-quotations'],
          permissionId: PERMISSIONS_VENTAS.VER_COTIZACIONES_DE_ENTREGA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Logistica',
      icon: 'pi pi-delivery',
      moduleType: 'sales',
      permissionModuleId: PERMISSION_MODULES.VENTAS,
      items: [
        {
          label: 'Guías de Despacho',
          icon: 'pi pi-fw pi-truck',
          routerLink: ['/sales/dispatch-guides'],
          permissionId: PERMISSIONS_VENTAS.VER_GUIAS_DE_DESPACHO,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Rutas de Despacho',
          icon: 'pi pi-fw pi-map',
          routerLink: ['/sales/dispatch-routes'],
          permissionId: PERMISSIONS_VENTAS.VER_RUTAS_DE_DESPACHO,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Vehículos',
          icon: 'pi pi-fw pi-car',
          routerLink: ['/sales/vehicles'],
          permissionId: PERMISSIONS_VENTAS.VER_VEHICULOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Choferes',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/sales/drivers'],
          permissionId: PERMISSIONS_VENTAS.VER_CHOFERES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Configuración de Ventas',
      icon: 'pi pi-cog',
      moduleType: 'sales',
      permissionModuleId: PERMISSION_MODULES.VENTAS,
      items: [
        {
          label: 'Fletes',
          icon: 'pi pi-fw pi-truck',
          routerLink: ['/sales/freights'],
          permissionId: PERMISSIONS_VENTAS.VER_FLETES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Descuentos',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/sales/discounts'],
          permissionId: PERMISSIONS_VENTAS.VER_DESCUENTOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Reportes',
      icon: 'pi pi-chart-line',
      moduleType: 'sales',
      permissionModuleId: PERMISSION_MODULES.VENTAS,
      items: [
        {
          label: 'Reportes de Ventas',
          icon: 'pi pi-fw pi-chart-line',
          items: [
            {
              label: 'Ventas hoy',
              icon: 'pi pi-fw pi-calendar',
              routerLink: ['/sales/reports/reports-dashboard'],
              permissionId: PERMISSIONS_VENTAS.VER_PANEL_RESUMEN,
            },
            {
              label: 'Ventas por Período',
              icon: 'pi pi-fw pi-calendar-clock',
              routerLink: ['/sales/reports/sales-by-period'],
              permissionId: PERMISSIONS_VENTAS.REPORTE_VENTAS_POR_PERIODO,
            },
            {
              label: 'Ventas por Vendedor',
              icon: 'pi pi-fw pi-users',
              routerLink: ['/sales/reports/sales-by-seller'],
              permissionId: PERMISSIONS_VENTAS.REPORTE_VENTAS_POR_VENDEDOR,
            },
            {
              label: 'Ventas por Producto',
              icon: 'pi pi-fw pi-box',
              routerLink: ['/sales/reports/sales-by-product'],
              permissionId: PERMISSIONS_VENTAS.REPORTE_VENTAS_POR_PRODUCTO,
            },
            {
              label: 'Análisis Geográfico',
              icon: 'pi pi-fw pi-map',
              routerLink: ['/sales/reports/geographic-analysis'],
              permissionId: PERMISSIONS_VENTAS.ANALISIS_GEOGRAFICO,
            },
            {
              label: 'Rentabilidad por Categoría',
              icon: 'pi pi-fw pi-percentage',
              routerLink: ['/sales/reports/profitability'],
              permissionId: PERMISSIONS_VENTAS.RENTABILIDAD_POR_CATEGORIA,
            },
            {
              label: 'Tendencias y Comparación',
              icon: 'pi pi-fw pi-chart-scatter',
              routerLink: ['/sales/reports/trends-analysis'],
              permissionId: PERMISSIONS_VENTAS.TENDENCIAS_Y_COMPARACION,
            },
            {
              label: 'Frecuencia de Compra',
              icon: 'pi pi-fw pi-refresh',
              routerLink: ['/sales/reports/customer-frequency'],
              permissionId: PERMISSIONS_VENTAS.FRECUENCIA_DE_COMPRA,
            },
            {
              label: 'Top Clientes',
              icon: 'pi pi-fw pi-star',
              routerLink: ['/sales/reports/top-customers'],
              permissionId: PERMISSIONS_VENTAS.TOP_CLIENTES,
            },
            {
              label: 'Ventas Detalladas',
              icon: 'pi pi-fw pi-wallet',
              routerLink: ['/sales/reports/detailed-sales'],
              permissionId: PERMISSIONS_VENTAS.REPORTE_DETALLADO_DE_VENTAS,
            },
          ],
        },
        { separator: true },
      ],
    },

    // ========================================
    // COMPRAS
    // ========================================
    {
      label: 'Compras',
      icon: 'pi pi-cart-plus',
      moduleType: 'purchases',
      permissionModuleId: PERMISSION_MODULES.COMPRAS,
      items: [
        {
          label: 'Solicitudes de Materiales',
          icon: 'pi pi-fw pi-file-arrow-up',
          routerLink: ['/purchases/material-requests'],
          permissionId: PERMISSIONS_COMPRAS.VER_SOLICITUDES_DE_MATERIALES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Solicitudes de Cotización',
          icon: 'pi pi-fw pi-file-check',
          routerLink: ['/purchases/procurement/rfq-list'],
          permissionId: PERMISSIONS_COMPRAS.VER_RFQS_Y_COTIZACIONES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Cotizaciones de compras',
          icon: 'pi pi-fw pi-file-import',
          routerLink: ['/purchases/procurement/quotations-list'],
          permissionId: PERMISSIONS_COMPRAS.VER_COTIZACIONES_DE_PROVEEDOR,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Órdenes de Compra',
          icon: 'pi pi-fw pi-shopping-cart',
          routerLink: ['/purchases/purchase-orders'],
          permissionId: PERMISSIONS_COMPRAS.VER_RDENES_DE_COMPRA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Recepciones',
          icon: 'pi pi-fw pi-inbox',
          routerLink: ['/purchases/purchase-receipts'],
          permissionId: PERMISSIONS_COMPRAS.VER_RECEPCIONES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Costos Adicionales de Compra',
          icon: 'pi pi-fw pi-dollar',
          routerLink: ['/purchases/landed-cost-vouchers'],
          permissionId: PERMISSIONS_COMPRAS.VER_COSTOS_ADICIONALES_DE_COMPRA,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Facturas de Proveedor',
          icon: 'pi pi-fw pi-file',
          routerLink: ['/purchases/vendor-bills'],
          permissionId: PERMISSIONS_COMPRAS.VER_FACTURAS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Conciliación de Compras',
          icon: 'pi pi-fw pi-link',
          routerLink: ['/purchases/purchase-matching'],
          permissionId: PERMISSIONS_COMPRAS.VER_CONCILIACION,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Devoluciones',
          icon: 'pi pi-fw pi-undo',
          routerLink: ['/purchases/supplier-returns'],
          permissionId: PERMISSIONS_COMPRAS.VER_DEVOLUCIONES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Proveedores',
      icon: 'pi pi-briefcase',
      moduleType: 'purchases',
      permissionModuleId: PERMISSION_MODULES.COMPRAS,
      items: [
        {
          label: 'Proveedores',
          icon: 'pi pi-fw pi-briefcase',
          routerLink: ['/purchases/suppliers'],
          permissionId: PERMISSIONS_CRM.VER_PROVEEDORES,
        },
        {
          label: 'Grupos de Proveedores',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/purchases/suppliers/groups'],
          permissionId: PERMISSIONS_CRM.VER_GRUPOS_DE_PROVEEDORES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Reportes',
      icon: 'pi pi-chart-bar',
      moduleType: 'purchases',
      permissionModuleId: PERMISSION_MODULES.COMPRAS,
      items: [
        {
          label: 'Reportes de Compras',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/purchases/purchase-reports'],
          permissionId: PERMISSIONS_COMPRAS.VER_REPORTES_ANALITICOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Precios por Artículo',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/purchases/item-prices'],
          permissionId: PERMISSIONS_COMPRAS.VER_PRECIOS_DE_TEMS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },

    // ========================================
    // INVENTARIO
    // ========================================
    {
      label: 'Inventario',
      icon: 'pi pi-box',
      moduleType: 'inventory',
      permissionModuleId: 'erp-inventario',
      items: [
        {
          label: 'Productos',
          icon: 'pi pi-fw pi-box',
          routerLink: ['/inventory/products'],
          permissionId: PERMISSIONS_INVENTARIO.VER_PRODUCTOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Precios',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/inventory/prices'],
          permissionId: PERMISSIONS_INVENTARIO.VER_PRECIOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Balance de Inventarios',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/inventory/movements/balances'],
          permissionId: PERMISSIONS_INVENTARIO.VER_BALANCES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Movimientos',
          icon: 'pi pi-fw pi-arrow-right-arrow-left',
          routerLink: ['/inventory/movements/list'],
          permissionId: PERMISSIONS_INVENTARIO.VER_MOVIMIENTOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Libro de Movimientos',
          icon: 'pi pi-fw pi-book',
          routerLink: ['/inventory/movements/ledger'],
          permissionId: PERMISSIONS_INVENTARIO.VER_MOVIMIENTOS,
        },
        {
          label: 'Solicitudes de Traslado',
          icon: 'pi pi-fw pi-truck',
          routerLink: ['/inventory/transfer-requests'],
          permissionId: PERMISSIONS_INVENTARIO.VER_MOVIMIENTOS,
        },
        {
          label: 'Transformaciones de Stock',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/inventory/stock-transformations'],
          permissionId: PERMISSIONS_INVENTARIO.VER_TRANSFORMACIONES_DE_STOCK,
        },
        {
          label: 'Lista de Almacenes',
          icon: 'pi pi-fw pi-warehouse',
          routerLink: ['/inventory/warehouses'],
          permissionId: PERMISSIONS_INVENTARIO.VER_ALMACENES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Catálogo',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/inventory/catalogo'],
          permissionId: PERMISSIONS_INVENTARIO.VER_CATALOGO,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Reporte de Valorización',
      icon: 'pi pi-chart-line',
      moduleType: 'inventory',
      permissionModuleId: PERMISSION_MODULES.INVENTARIO,
      items: [
        {
          label: 'Resumen',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/inventory/valuation/summary'],
          permissionId: PERMISSIONS_INVENTARIO.VER_VALORIZACION_DE_INVENTARIO,
        },
        {
          label: 'Por Categoría',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/inventory/valuation/by-category'],
          permissionId: PERMISSIONS_INVENTARIO.VER_VALORIZACION_DE_INVENTARIO,
        },
        {
          label: 'Por Producto',
          icon: 'pi pi-fw pi-box',
          routerLink: ['/inventory/valuation/by-product'],
          permissionId: PERMISSIONS_INVENTARIO.VER_VALORIZACION_DE_INVENTARIO,
        },
        {
          label: 'Por Almacén',
          icon: 'pi pi-fw pi-warehouse',
          routerLink: ['/inventory/valuation/by-warehouse'],
          permissionId: PERMISSIONS_INVENTARIO.VER_VALORIZACION_DE_INVENTARIO,
        },
      ],
    },
    {
      label: 'Configuración de Inventario',
      icon: 'pi pi-cog',
      moduleType: 'inventory',
      permissionModuleId: PERMISSION_MODULES.INVENTARIO,
      items: [
        {
          label: 'Categorías',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/inventory/categories'],
          permissionId: PERMISSIONS_INVENTARIO.VER_CATEGORIAS,
        },
        {
          label: 'Unidades de Medida',
          icon: 'pi pi-fw pi-calculator',
          routerLink: ['/inventory/units-of-measure'],
          permissionId: PERMISSIONS_INVENTARIO.VER_UNIDADES_DE_MEDIDA,
        },
        {
          label: 'Atributos',
          icon: 'pi pi-fw pi-sliders-h',
          routerLink: ['/inventory/attributes'],
          permissionId: PERMISSIONS_INVENTARIO.VER_ATRIBUTOS,
        },
        {
          label: 'Alertas',
          icon: 'pi pi-fw pi-bell',
          routerLink: ['/inventory/alert-config'],
          permissionId: PERMISSIONS_INVENTARIO.VER_ALERTAS,
        },
        {
          label: 'Diagnóstico',
          icon: 'pi pi-fw pi-wave-pulse',
          routerLink: ['/inventory/diagnostic'],
          permissionId: PERMISSIONS_INVENTARIO.VER_DIAGNOSTICO_DE_INVENTARIO,
        },
      ],
    },

    // ========================================
    // CRM (Clientes, Proveedores, Vendedores)
    // ========================================
    {
      label: 'CRM',
      icon: 'pi pi-users',
      moduleType: 'crm',
      permissionModuleId: PERMISSION_MODULES.CRM,
      items: [
        {
          label: 'Clientes',
          icon: 'pi pi-fw pi-user',
          routerLink: ['/crm/customers'],
          permissionId: PERMISSIONS_CRM.VER_CLIENTES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Lineas de Crédito',
          icon: 'pi pi-fw pi-credit-card',
          routerLink: ['/crm/credit-lines'],
          permissionId: PERMISSIONS_CRM.VER_LINEAS_DE_CREDITO,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Proveedores',
          icon: 'pi pi-fw pi-briefcase',
          routerLink: ['/crm/suppliers'],
          permissionId: PERMISSIONS_CRM.VER_PROVEEDORES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Grupos de Proveedores',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/crm/supplier-groups'],
          permissionId: PERMISSIONS_CRM.VER_GRUPOS_DE_PROVEEDORES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Colaboradores',
          icon: 'pi pi-fw pi-user-plus',
          routerLink: ['/crm/collaborators'],
          permissionId: PERMISSIONS_CRM.VER_COLABORADORES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Vendedores',
      icon: 'pi pi-users',
      moduleType: 'crm',
      permissionModuleId: PERMISSION_MODULES.CRM,
      items: [
        {
          label: 'Lista de Vendedores',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/crm/sellers'],
          permissionId: PERMISSIONS_CRM.VER_VENDEDORES,
        },
        {
          label: 'Lista de Usuarios de Vendedores',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/crm/sellers/users-sellers-list'],
          permissionId: PERMISSIONS_CRM.VER_VENDEDORES,
        },
        {
          label: 'Comisiones',
          icon: 'pi pi-fw pi-dollar',
          routerLink: ['/crm/commissions'],
          permissionId: PERMISSIONS_CRM.VER_COMISIONES,
        },
        {
          label: 'Comisiones por Categoría',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/crm/commissions/category'],
          permissionId: PERMISSIONS_CRM.VER_CONFIGURACION_DE_COMISIONES_POR_CATEGORIA,
        },
        {
          label: 'Comisiones Automáticas',
          icon: 'pi pi-fw pi-sync',
          routerLink: ['/crm/commissions/config'],
          permissionId: PERMISSIONS_CRM.VER_CONFIGURACION_DE_COMISIONES_AUTOMATICA,
        },
      ],
    },

    // ========================================
    // Finanzas
    // ========================================
    {
      label: 'Tesoreria',
      icon: 'pi pi-wallet',
      moduleType: 'finance',
      permissionModuleId: PERMISSION_MODULES.FINANZAS,
      items: [
        {
          label: 'Cuentas',
          icon: 'pi pi-fw pi-book',
          routerLink: ['/finance/accounts'],
          permissionId: PERMISSIONS_FINANZAS.VER_CUENTAS_BANCARIAS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Historial de Movimientos',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/finance/transactions'],
          permissionId: PERMISSIONS_FINANZAS.VER_MOVIMIENTOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'CxC y CxP',
      icon: 'pi pi-wallet',
      moduleType: 'finance',
      permissionModuleId: PERMISSION_MODULES.FINANZAS,
      items: [
        {
          label: 'Resumen',
          icon: 'pi pi-fw pi-chart-line',
          // Sin `requiresFullTenantScope` desde 2026-08-05: el backend acota `/dashboard` por el
          // `sellerId` del JWT, así que un vendedor ve aquí su propia cartera (verificado en vivo).
          routerLink: ['/finance/arp/dashboard'],
          permissionId: PERMISSIONS_FINANZAS.VER_CUENTAS_POR_COBRAR_PAGAR,
        },
        {
          label: 'Cobros del día',
          icon: 'pi pi-fw pi-money-bill',
          routerLink: ['/finance/arp/payments'],
          permissionId: PERMISSIONS_FINANZAS.VER_CUENTAS_POR_COBRAR_PAGAR,
        },
        {
          label: 'Cuentas por Cobrar/Pagar',
          icon: 'pi pi-fw pi-bookmark',
          routerLink: ['/finance/arp'],
          permissionId: PERMISSIONS_FINANZAS.VER_CUENTAS_POR_COBRAR_PAGAR,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Categorías de cuentas',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/finance/arp/categories'],
          permissionId: PERMISSIONS_FINANZAS.VER_CATEGORIAS_DE_CUENTAS,
        },
        {
          label: 'Solicitudes de Cobro',
          icon: 'pi pi-fw pi-file-check',
          routerLink: ['/finance/payment-requests'],
          permissionIdsAny: [
            PERMISSIONS_FINANZAS.VER_SOLICITUDES_DE_PAGO,
            PERMISSIONS_FINANZAS.VER_TODAS_LAS_SOLICITUDES_DE_PAGO,
          ],
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Anticipos de Clientes',
          icon: 'pi pi-fw pi-money-bill',
          routerLink: ['/finance/arp/advances'],
          permissionId: PERMISSIONS_FINANZAS.VER_ANTICIPOS_DE_CLIENTES,
        },
        {
          label: 'Anticipos a Proveedores',
          icon: 'pi pi-fw pi-send',
          routerLink: ['/finance/arp/supplier-advances'],
          permissionId: PERMISSIONS_FINANZAS.VER_ANTICIPOS_A_PROVEEDORES,
        },
      ],
    },
    {
      label: 'Gastos',
      icon: 'pi pi-tag',
      moduleType: 'finance',
      permissionModuleId: PERMISSION_MODULES.GASTOS,
      items: [
        {
          label: 'Resumen',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/finance/expenses/dashboard'],
          permissionId: PERMISSIONS_GASTOS.VER_DASHBOARD_DE_GASTOS,
        },
        {
          label: 'Lista de Gastos',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/finance/expenses/list'],
          permissionId: PERMISSIONS_GASTOS.VER_GASTOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Gastos Fijos',
          icon: 'pi pi-fw pi-clock',
          routerLink: ['/finance/expenses/fixed'],
          permissionId: PERMISSIONS_GASTOS.VER_PLANTILLAS_DE_GASTO_FIJO,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Categorías de gastos',
          icon: 'pi pi-fw pi-tag',
          routerLink: ['/finance/expenses/categories'],
          permissionId: PERMISSIONS_GASTOS.GESTIONAR_CATEGORIAS_DE_GASTO,
        },
      ],
    },
    {
      label: 'Reportes',
      icon: 'pi pi-chart-line',
      moduleType: 'finance',
      permissionModuleId: PERMISSION_MODULES.FINANZAS,
      items: [
        {
          label: 'Reporte de Antigüedad',
          icon: 'pi pi-fw pi-clock',
          routerLink: ['/finance/reports/aging'],
          // El endpoint exige además `finanzas-cuentas-cobrar-pagar-ver` desde 2026-08-05.
          permissionIdsAll: [
            PERMISSIONS_FINANZAS.VER_ANTIGUEDAD_DE_SALDOS,
            PERMISSIONS_FINANZAS.VER_CUENTAS_POR_COBRAR_PAGAR,
          ],
        },
        {
          label: 'Estado de Cuenta',
          icon: 'pi pi-fw pi-file',
          routerLink: ['/finance/reports/account-statement'],
          permissionIdsAll: [
            PERMISSIONS_FINANZAS.VER_ESTADO_DE_CUENTA,
            PERMISSIONS_FINANZAS.VER_CUENTAS_POR_COBRAR_PAGAR,
          ],
        },
        {
          label: 'Libro de Banco',
          icon: 'pi pi-fw pi-building',
          routerLink: ['/finance/reports/bank-book'],
          permissionId: PERMISSIONS_FINANZAS.VER_LIBRO_DE_BANCO,
        },
        {
          label: 'Flujo de Caja',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/finance/reports/cash-flow'],
          permissionId: PERMISSIONS_FINANZAS.VER_FLUJO_DE_CAJA,
        },
        {
          label: 'Ingresos vs Egresos',
          icon: 'pi pi-fw pi-chart-pie',
          routerLink: ['/finance/reports/income-expense'],
          permissionId: PERMISSIONS_FINANZAS.VER_INGRESOS_Y_EGRESOS,
        },
        {
          label: 'Posición Cambiaria',
          icon: 'pi pi-fw pi-sync',
          routerLink: ['/finance/reports/currency-position'],
          permissionId: PERMISSIONS_FINANZAS.VER_POSICION_CAMBIARIA,
        },
        {
          // «Operativa» no es un adorno: el módulo de Contabilidad tiene otra «Salud Financiera»
          // (`/accounting/financial-health`, score sobre balance y estado de resultados). Sin el
          // apellido, dos ítems del menú se llamarían igual y medirían cosas distintas.
          label: 'Salud Financiera Operativa',
          icon: 'pi pi-fw pi-heart-fill',
          routerLink: ['/finance/reports/financial-health'],
          permissionId: PERMISSIONS_FINANZAS.VER_SALUD_FINANCIERA,
        },
      ],
    },

    // ========================================
    // Fabricación
    // ========================================
    {
      label: 'Fabricación',
      icon: 'pi pi-cog',
      moduleType: 'manufacturing',
      permissionModuleId: PERMISSION_MODULES.FABRICACION,
      items: [
        {
          label: 'Recetas y BOM',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/manufacturing/boms'],
          permissionId: PERMISSIONS_FABRICACION.VER_RECETAS_Y_BOM,
        },
        {
          label: 'Órdenes de producción',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/manufacturing/production-orders'],
          permissionId: PERMISSIONS_FABRICACION.VER_RDENES_DE_PRODUCCION,
        },
        {
          label: 'Consumos internos',
          icon: 'pi pi-fw pi-box',
          routerLink: ['/manufacturing/internal-consumptions'],
          permissionId: PERMISSIONS_FABRICACION.VER_CONSUMOS_INTERNOS,
        },
      ],
    },

    // ========================================
    // Contabilidad
    // ========================================
    {
      label: 'Operación Contable',
      icon: 'pi pi-book',
      moduleType: 'accounting',
      permissionModuleId: PERMISSION_MODULES.CONTABILIDAD,
      items: [
        {
          label: 'Dashboard Contable',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/accounting/dashboard'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_CONFIGURACION_CONTABLE,
            PERMISSIONS_CONTABILIDAD.VER_REPORTES_CONTABLES,
          ],
        },
        {
          label: 'Asientos Contables',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/accounting/journal-entries'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_ASIENTOS,
            PERMISSIONS_CONTABILIDAD.CREAR_ASIENTOS,
            PERMISSIONS_CONTABILIDAD.POSTEAR_ASIENTOS,
            PERMISSIONS_CONTABILIDAD.EDITAR_BORRADOR_DE_ASIENTO,
            PERMISSIONS_CONTABILIDAD.ANULAR_BORRADOR,
          ],
        },
        {
          label: 'Libro Diario',
          icon: 'pi pi-fw pi-book',
          routerLink: ['/accounting/journal-book'],
          permissionIdsAny: [PERMISSIONS_CONTABILIDAD.VER_REPORTES_CONTABLES],
        },
        {
          label: 'Saldos Iniciales',
          icon: 'pi pi-fw pi-database',
          routerLink: ['/accounting/opening-balances'],
          permissionIdsAny: [PERMISSIONS_CONTABILIDAD.VER_ASIENTOS, PERMISSIONS_CONTABILIDAD.CREAR_ASIENTOS],
        },
        {
          label: 'Periodos',
          icon: 'pi pi-fw pi-calendar',
          routerLink: ['/accounting/periods'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_PERIODOS,
            PERMISSIONS_CONTABILIDAD.GENERAR_PERIODOS,
            PERMISSIONS_CONTABILIDAD.CERRAR_PERIODOS,
            PERMISSIONS_CONTABILIDAD.REABRIR_PERIODOS,
          ],
        },
      ],
    },
    {
      label: 'Análisis y Reportes',
      icon: 'pi pi-chart-bar',
      moduleType: 'accounting',
      permissionModuleId: PERMISSION_MODULES.CONTABILIDAD,
      items: [
        {
          label: 'Salud Financiera',
          icon: 'pi pi-fw pi-heart',
          routerLink: ['/accounting/financial-health'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_REPORTES_CONTABLES,
            PERMISSIONS_CONTABILIDAD.VER_CONFIGURACION_CONTABLE,
          ],
        },
        {
          label: 'Reportes Contables',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/accounting/reports/trial-balance'],
          permissionIdsAny: [PERMISSIONS_CONTABILIDAD.VER_REPORTES_CONTABLES],
        },
      ],
    },
    {
      label: 'Configuración Contable',
      icon: 'pi pi-cog',
      moduleType: 'accounting',
      permissionModuleId: PERMISSION_MODULES.CONTABILIDAD,
      items: [
        {
          label: 'Plan de Cuentas',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/accounting/chart-of-accounts'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_PLAN_DE_CUENTAS,
            PERMISSIONS_CONTABILIDAD.CREAR_CUENTAS,
            PERMISSIONS_CONTABILIDAD.EDITAR_CUENTAS,
            PERMISSIONS_CONTABILIDAD.SEMBRAR_PLAN_VENEZUELA,
          ],
        },
        {
          label: 'Reglas Operativas',
          icon: 'pi pi-fw pi-sliders-h',
          routerLink: ['/accounting/dimension-mappings'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_MAPEOS_FISCALES,
            PERMISSIONS_CONTABILIDAD.GESTIONAR_MAPEOS_FISCALES,
          ],
        },
        {
          label: 'Reglas de Contabilización',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/accounting/posting-rules'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_REGLAS_DE_POSTING,
            PERMISSIONS_CONTABILIDAD.GESTIONAR_REGLAS_DE_POSTING,
          ],
        },
        {
          label: 'Activos Fijos',
          icon: 'pi pi-fw pi-box',
          routerLink: ['/accounting/fixed-assets'],
          // Solo el permiso que acepta el guard de la ruta: con los cuatro, un rol con
          // "Correr Depreciación" pero sin "Ver" veía la entrada y el guard lo rechazaba.
          permissionIdsAny: [PERMISSIONS_CONTABILIDAD.VER_ACTIVOS_FIJOS],
        },
        {
          label: 'Ajuste por Inflación',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/accounting/inflation'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_CONFIGURACION_CONTABLE,
            PERMISSIONS_CONTABILIDAD.EDITAR_CONFIGURACION_CONTABLE,
            PERMISSIONS_CONTABILIDAD.VER_REPORTES_CONTABLES,
          ],
        },
        {
          label: 'Parámetros Contables',
          icon: 'pi pi-fw pi-wrench',
          routerLink: ['/accounting/settings'],
          permissionIdsAny: [
            PERMISSIONS_CONTABILIDAD.VER_CONFIGURACION_CONTABLE,
            PERMISSIONS_CONTABILIDAD.EDITAR_CONFIGURACION_CONTABLE,
          ],
        },
      ],
    },

    // ========================================
    // E-COMMERCE
    // ========================================
    {
      label: 'Tienda en Línea',
      icon: 'pi pi-shopping-cart',
      moduleType: 'ecommerce',
      permissionModuleId: PERMISSION_MODULES.E_COMMERCE,
      items: [
        {
          label: 'Reportes',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/ecommerce/reports/dashboard'],
          permissionId: PERMISSIONS_E_COMMERCE.VER_REPORTES_E_COMMERCE,
        },
        {
          label: 'Ventas',
          icon: 'pi pi-fw pi-shopping-bag',
          routerLink: ['/ecommerce/reports/sales'],
          permissionId: PERMISSIONS_E_COMMERCE.VER_REPORTES_E_COMMERCE,
        },
        {
          label: 'Top Rankings',
          icon: 'pi pi-fw pi-star',
          routerLink: ['/ecommerce/reports/tops'],
          permissionId: PERMISSIONS_E_COMMERCE.VER_REPORTES_E_COMMERCE,
        },
        {
          label: 'Clientes',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/ecommerce/customers'],
          permissionId: PERMISSIONS_E_COMMERCE.VER_CUENTAS_DE_CLIENTES,
        },
        {
          label: 'Categorías',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/ecommerce/categories'],
          permissionId: PERMISSIONS_E_COMMERCE.PUBLICAR_ARTICULOS_Y_CATEGORIAS,
        },
        {
          label: 'Productos',
          icon: 'pi pi-fw pi-box',
          routerLink: ['/ecommerce/products'],
          permissionId: PERMISSIONS_E_COMMERCE.PUBLICAR_ARTICULOS_Y_CATEGORIAS,
        },
        {
          label: 'Configuración',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/ecommerce/config'],
          permissionId: PERMISSIONS_E_COMMERCE.VER_CONFIGURACION_E_COMMERCE,
        },
      ],
    },

    { separator: true },

    // ========================================
    // RECURSOS HUMANOS
    // Reorganizado por jerarquía operativa:
    //   1. Indicadores
    //   2. Estructura Organizacional
    //   3. Personal
    //   4. Contratación
    //   5. Jornadas y Ausencias
    //   6. Políticas
    // ========================================
    {
      label: 'Indicadores',
      icon: 'pi pi-chart-bar',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/rrhh/dashboard'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_DASHBOARD,
        },
        {
          label: 'Pasivo Vacacional',
          icon: 'pi pi-fw pi-money-bill',
          routerLink: ['/rrhh/reports'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_PASIVO_VACACIONAL_RRHH,
        },
      ],
    },
    {
      label: 'Estructura Organizacional',
      icon: 'pi pi-sitemap',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Centros de Costo',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/rrhh/cost-centers'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_CENTROS_DE_COSTO,
        },
        {
          label: 'Departamentos',
          icon: 'pi pi-fw pi-building',
          routerLink: ['/rrhh/departments'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_DEPARTAMENTOS,
        },
        {
          label: 'Puestos',
          icon: 'pi pi-fw pi-briefcase',
          routerLink: ['/rrhh/positions'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_POSICIONES,
        },
      ],
    },
    {
      label: 'Personal',
      icon: 'pi pi-users',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Tipos de Empleado',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/rrhh/employee-types'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_TIPOS_DE_EMPLEADO,
        },
        {
          label: 'Empleados',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/rrhh/employees'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_EMPLEADOS,
        },
        {
          label: 'Familiares',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/rrhh/family-members'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_FAMILIARES,
        },
      ],
    },
    {
      label: 'Contratación',
      icon: 'pi pi-briefcase',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Tipos de Contrato',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/rrhh/contract-types'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_TIPOS_DE_CONTRATO,
        },
        {
          label: 'Cláusulas de Contratos',
          icon: 'pi pi-fw pi-book',
          routerLink: ['/rrhh/contract-clauses'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_CLAUSULAS_DE_CONTRATO,
        },
        {
          label: 'Plantillas de Contratos',
          icon: 'pi pi-fw pi-copy',
          routerLink: ['/rrhh/contract-templates'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_PLANTILLAS_DE_CONTRATO,
        },
        {
          label: 'Contratos',
          icon: 'pi pi-fw pi-file-o',
          routerLink: ['/rrhh/contracts'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_CONTRATOS,
        },
        {
          label: 'Anexos de Contratos',
          icon: 'pi pi-fw pi-folder-open',
          routerLink: ['/rrhh/contracts/annexes'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_CONTRATOS,
        },
      ],
    },
    {
      label: 'Jornadas y Ausencias',
      icon: 'pi pi-calendar-clock',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Turnos',
          icon: 'pi pi-fw pi-clock',
          routerLink: ['/rrhh/shifts'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_TURNOS,
        },
        {
          label: 'Horarios',
          icon: 'pi pi-fw pi-calendar',
          routerLink: ['/rrhh/work-schedules'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_HORARIOS,
        },
        {
          label: 'Cuadrillas',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/rrhh/work-crews'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_HORARIOS,
        },
        {
          label: 'Feriados',
          icon: 'pi pi-fw pi-calendar-plus',
          routerLink: ['/rrhh/holidays'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_FERIADOS,
        },
        {
          label: 'Ausencias',
          icon: 'pi pi-fw pi-calendar-times',
          routerLink: ['/rrhh/absences'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_AUSENCIAS,
        },
        {
          label: 'Suspensiones',
          icon: 'pi pi-fw pi-pause-circle',
          routerLink: ['/rrhh/suspensions'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_SUSPENSIONES,
        },
        {
          label: 'Vacaciones',
          icon: 'pi pi-fw pi-sun',
          routerLink: ['/rrhh/vacations'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_VACACIONES,
        },
      ],
    },
    {
      label: 'Políticas',
      icon: 'pi pi-sliders-h',
      moduleType: 'rrhh',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
      items: [
        {
          label: 'Reglas de Vacaciones',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/rrhh/vacation-rules'],
          permissionId: PERMISSIONS_RECURSOS_HUMANOS.VER_REGLAS_DE_VACACIONES,
        },
      ],
    },

    { separator: true },

    // ========================================
    // NÓMINA
    // Reorganizado en 4 grupos por fase del proceso:
    //   1. Ciclo del Período   — lo más usado (crear/calcular/cerrar/pagar)
    //   2. Procesos Especiales — liquidaciones, estimaciones ISLR
    //   3. Configuración       — catálogo de conceptos, asignaciones, legal
    //   4. Reportes y Auditoría
    // ========================================
    {
      label: 'Procesar Nómina',
      icon: 'pi pi-money-bill',
      moduleType: 'payroll',
      permissionModuleId: PERMISSION_MODULES.NOMINA,
      items: [
        {
          label: 'Tablero',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/payroll/dashboard'],
        },
        {
          label: 'Periodos',
          icon: 'pi pi-fw pi-calendar',
          routerLink: ['/payroll/periods'],
          permissionId: PERMISSIONS_NOMINA.VER_PERIODOS,
        },
        {
          label: 'Novedades del Período',
          icon: 'pi pi-fw pi-bell',
          routerLink: ['/payroll/incidents'],
          permissionId: PERMISSIONS_NOMINA.VER_NOVEDADES,
        },
        {
          label: 'Préstamos',
          icon: 'pi pi-fw pi-wallet',
          routerLink: ['/payroll/loans'],
          permissionId: PERMISSIONS_NOMINA.VER_PRESTAMOS,
        },
        {
          label: 'Prevalidación',
          icon: 'pi pi-fw pi-verified',
          routerLink: ['/payroll/health'],
          permissionId: PERMISSIONS_NOMINA.VER_SALUD_DE_NOMINA,
        },
        {
          label: 'Recibos de Pago',
          icon: 'pi pi-fw pi-file-pdf',
          routerLink: ['/payroll/transactions'],
          permissionId: PERMISSIONS_NOMINA.VER_RECIBOS_DE_PAGO,
        },
      ],
    },
    {
      label: 'Liquidaciones e ISLR',
      icon: 'pi pi-briefcase',
      moduleType: 'payroll',
      permissionModuleId: PERMISSION_MODULES.NOMINA,
      items: [
        {
          label: 'Liquidaciones (LOTTT)',
          icon: 'pi pi-fw pi-file-excel',
          routerLink: ['/payroll/settlements'],
          permissionId: PERMISSIONS_NOMINA.VER_LIQUIDACIONES,
        },
        {
          label: 'Estimaciones ISLR (ARI)',
          icon: 'pi pi-fw pi-percentage',
          routerLink: ['/payroll/islr-estimations'],
          permissionId: PERMISSIONS_NOMINA.VER_ESTIMACIONES_ISLR,
        },
      ],
    },
    {
      label: 'Configuración',
      icon: 'pi pi-cog',
      moduleType: 'payroll',
      permissionModuleId: PERMISSION_MODULES.NOMINA,
      items: [
        {
          label: 'Catálogo de Conceptos',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/payroll/concepts'],
          permissionId: PERMISSIONS_NOMINA.VER_CONCEPTOS,
        },
        {
          label: 'Asignaciones por Empleado',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/payroll/employee-concepts'],
          permissionId: PERMISSIONS_NOMINA.VER_CONCEPTOS_POR_EMPLEADO,
        },
        {
          label: 'Catálogos Legales (ISLR/IVSS/UT)',
          icon: 'pi pi-fw pi-book',
          routerLink: ['/payroll/legal-catalogs'],
          permissionId: PERMISSIONS_NOMINA.VER_CATALOGOS_LEGALES,
        },
      ],
    },
    {
      label: 'Reportes y Auditoría',
      icon: 'pi pi-chart-line',
      moduleType: 'payroll',
      permissionModuleId: PERMISSION_MODULES.NOMINA,
      items: [
        {
          label: 'Centro de Reportes',
          icon: 'pi pi-fw pi-print',
          routerLink: ['/payroll/reports'],
          permissionId: PERMISSIONS_NOMINA.VER_LIBRO_DE_NOMINA,
        },
        {
          label: 'Auditoría',
          icon: 'pi pi-fw pi-shield',
          routerLink: ['/payroll/audit'],
          permissionId: PERMISSIONS_NOMINA.VER_AUDITORIA_DE_NOMINA,
        },
      ],
    },

    { separator: true },

    // ========================================
    // CONFIGURACIÓN GENERAL
    // ========================================
    {
      label: 'Configuración General',
      icon: 'pi pi-cog',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Configuración Principal',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/general-settings/settings'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_CONFIGURACION_PRINCIPAL,
        },
        {
          label: 'Datos Fiscales',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/general-settings/company/fiscal-data'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_DATOS_FISCALES,
        },
        {
          label: 'Estilo de Aplicación',
          icon: 'pi pi-fw pi-palette',
          routerLink: ['/general-settings/company/customization'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_ESTILO_DE_LA_APLICACION,
        },
        {
          label: 'Secuencias de Documentos',
          icon: 'pi pi-fw pi-receipt',
          routerLink: ['/general-settings/document-sequences'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_SECUENCIAS_DE_DOCUMENTOS,
        },
      ],
    },
    {
      label: 'Comercial',
      icon: 'pi pi-shopping-bag',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Sedes',
          icon: 'pi pi-fw pi-building',
          routerLink: ['/general-settings/branches'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_SUCURSALES,
        },
        {
          label: 'Listas de Precios',
          icon: 'pi pi-fw pi-money-bill',
          routerLink: ['/general-settings/prices-lists'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_LISTAS_DE_PRECIOS,
        },
        {
          label: 'Impuestos',
          icon: 'pi pi-fw pi-percentage',
          routerLink: ['/general-settings/taxes'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_IMPUESTOS,
        },
        {
          label: 'Términos de Pago',
          icon: 'pi pi-fw pi-clock',
          routerLink: ['/general-settings/payment-terms'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_TERMINOS_DE_PAGO,
        },
        {
          label: 'Métodos de Pago',
          icon: 'pi pi-fw pi-wallet',
          routerLink: ['/general-settings/payment-methods'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_METODOS_DE_PAGO,
        },
      ],
    },
    {
      label: 'Fiscal',
      icon: 'pi pi-calculator',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Configuración Fiscal',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/general-settings/fiscal-config'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_CONFIGURACION_FISCAL,
        },
        {
          label: 'Categorías de Impuestos',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/general-settings/tax-categories'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_CATEGORIAS_DE_IMPUESTOS,
        },
        {
          label: 'Unidad Tributaria',
          icon: 'pi pi-fw pi-calculator',
          routerLink: ['/general-settings/fiscal-unit-values'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_VALOR_UNIDAD_TRIBUTARIA,
        },
        {
          label: 'Alícuotas de Impuestos',
          icon: 'pi pi-fw pi-percentage',
          routerLink: ['/general-settings/tax-rates'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_TASAS_DE_IMPUESTOS,
        },
        {
          label: 'Conceptos ISLR',
          icon: 'pi pi-fw pi-file-minus',
          routerLink: ['/general-settings/islr-concepts'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_CONCEPTOS_ISLR,
        },
      ],
    },
    {
      label: 'Financiero',
      icon: 'pi pi-dollar',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Monedas',
          icon: 'pi pi-fw pi-dollar',
          routerLink: ['/general-settings/currencies-exchanges/currencies'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_MONEDAS,
        },
        {
          label: 'Tipos de Cambio',
          icon: 'pi pi-fw pi-refresh',
          routerLink: ['/general-settings/currencies-exchanges/exchange-rates'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_TIPOS_DE_CAMBIO,
        },
        {
          label: 'Series de Tasa',
          icon: 'pi pi-fw pi-chart-line',
          routerLink: ['/general-settings/currencies-exchanges/series'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_TIPOS_DE_CAMBIO,
        },
        {
          label: 'Historial de Tasas',
          icon: 'pi pi-fw pi-history',
          routerLink: ['/general-settings/currencies-exchanges/exchange-rates-history'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_TIPOS_DE_CAMBIO,
        },
      ],
    },
    {
      label: 'Geografía',
      icon: 'pi pi-map',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Zonas',
          icon: 'pi pi-fw pi-map',
          routerLink: ['/general-settings/zones/zones'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_ZONAS,
        },
        {
          label: 'Sub-zonas',
          icon: 'pi pi-fw pi-map-marker',
          routerLink: ['/general-settings/zones/sub-zones'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_SUB_ZONAS,
        },
      ],
    },
    {
      label: 'Seguridad',
      icon: 'pi pi-shield',
      moduleType: 'settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
      items: [
        {
          label: 'Usuarios',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/general-settings/users/users'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_USUARIOS,
        },
        {
          label: 'Roles y Permisos',
          icon: 'pi pi-fw pi-lock',
          routerLink: ['/general-settings/users/roles'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_ROLES_Y_PERMISOS,
        },
        {
          label: 'Registros de auditoría',
          icon: 'pi pi-fw pi-file',
          routerLink: ['/general-settings/audit-logs'],
          permissionId: PERMISSIONS_CONFIGURACIONES.VER_REGISTROS,
        },
      ],
    },

    // ========================================
    // TRIBUTARIO
    // ========================================
    {
      label: 'Retenciones',
      icon: 'pi pi-file-minus',
      moduleType: 'tributario',
      permissionModuleId: PERMISSION_MODULES.TRIBUTARIO,
      items: [
        {
          label: 'Comprobantes de Retención',
          icon: 'pi pi-fw pi-file-check',
          routerLink: ['/tributario/retenciones/comprobantes'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_RETENCIONES,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Retenciones Sufridas',
          icon: 'pi pi-fw pi-file-import',
          routerLink: ['/tributario/retenciones/sufridas'],
          // #456: igual que la ruta; el listado exige `ver` en el backend.
          permissionIdsAll: [
            PERMISSIONS_TRIBUTARIO.REGISTRAR_RETENCIONES_SUFRIDAS,
            PERMISSIONS_TRIBUTARIO.VER_RETENCIONES,
          ],
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Libros Fiscales',
      icon: 'pi pi-book',
      moduleType: 'tributario',
      permissionModuleId: PERMISSION_MODULES.TRIBUTARIO,
      items: [
        {
          label: 'Libro de Ventas',
          icon: 'pi pi-fw pi-shopping-cart',
          routerLink: ['/tributario/libros-fiscales/ventas'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
        {
          label: 'Libro de Compras',
          icon: 'pi pi-fw pi-cart-plus',
          routerLink: ['/tributario/libros-fiscales/compras'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
        {
          label: 'Retenciones IVA',
          icon: 'pi pi-fw pi-percentage',
          routerLink: ['/tributario/libros-fiscales/retenciones-iva'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
        {
          label: 'Retenciones ISLR',
          icon: 'pi pi-fw pi-percentage',
          routerLink: ['/tributario/libros-fiscales/retenciones-islr'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
        {
          label: 'Declaración de IVA',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/tributario/libros-fiscales/declaracion-iva'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
        {
          label: 'Declaración IGTF',
          icon: 'pi pi-fw pi-money-bill',
          routerLink: ['/tributario/libros-fiscales/igtf'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_LIBROS_FISCALES,
        },
      ],
    },
    {
      label: 'IGTF',
      icon: 'pi pi-money-bill',
      moduleType: 'tributario',
      permissionModuleId: PERMISSION_MODULES.TRIBUTARIO,
      items: [
        {
          label: 'Registros IGTF',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/tributario/igtf/registros'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_REGISTROS_IGTF,
        },
      ],
    },
    {
      label: 'Reportes y Seguridad',
      icon: 'pi pi-chart-bar',
      moduleType: 'tributario',
      permissionModuleId: PERMISSION_MODULES.TRIBUTARIO,
      items: [
        {
          label: 'Reportes Tributarios',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/tributario/reportes/resumen'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_REPORTES_TRIBUTARIOS,
        },
        {
          label: 'Seguridad Fiscal',
          icon: 'pi pi-fw pi-shield',
          routerLink: ['/tributario/seguridad-fiscal'],
          permissionId: PERMISSIONS_TRIBUTARIO.VER_PANEL_DE_SEGURIDAD_FISCAL,
        },
      ],
    },

    // ========================================
    // PUNTO DE VENTA - Configuración
    // ========================================
    {
      label: 'Punto de Venta',
      icon: 'pi pi-desktop',
      moduleType: 'pos',
      permissionModuleId: PERMISSION_MODULES.PUNTO_DE_VENTA,
      items: [
        {
          label: 'Abrir Caja',
          icon: 'pi pi-fw pi-play-circle',
          routerLink: ['/pos'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.ACCEDER_AL_MODULO_POS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        { separator: true },
        {
          label: 'Perfiles POS',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/pos/settings/profiles'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.GESTIONAR_PERFILES_POS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Cajas Registradoras',
          icon: 'pi pi-fw pi-desktop',
          routerLink: ['/pos/settings/registers'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.GESTIONAR_CAJAS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Cajeros',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/pos/settings/cashiers'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.GESTIONAR_PERMISOS_DE_CAJEROS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Permisos de Cajeros',
          icon: 'pi pi-fw pi-key',
          routerLink: ['/pos/settings/permissions'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.GESTIONAR_PERMISOS_DE_CAJEROS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
        {
          label: 'Historial de Sesiones',
          icon: 'pi pi-fw pi-history',
          routerLink: ['/pos/settings/sessions'],
          permissionId: PERMISSIONS_PUNTO_DE_VENTA.VER_HISTORIAL_DE_TURNOS,
          routerLinkActiveOptions: {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },

    // ========================================
    // PROYECTOS Y TAREAS (KANBAN)
    // ========================================
    {
      label: 'Gestión y Seguimiento',
      icon: 'pi pi-objects-column',
      moduleType: 'proyectos',
      permissionModuleId: PERMISSION_MODULES.PROYECTOS_Y_TAREAS,
      items: [
        {
          label: 'Resumen y Métricas',
          icon: 'pi pi-fw pi-chart-pie',
          routerLink: ['/proyectos/resumen'],
          permissionId: PERMISSIONS_PROYECTOS_Y_TAREAS.VER_PROYECTOS_Y_TAREAS,
        },
        {
          label: 'Cartera de Proyectos',
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/proyectos'],
          permissionId: PERMISSIONS_PROYECTOS_Y_TAREAS.VER_PROYECTOS_Y_TAREAS,
          routerLinkActiveOptions: {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          },
        },
      ],
    },
    {
      label: 'Espacio de Trabajo',
      icon: 'pi pi-briefcase',
      moduleType: 'proyectos',
      permissionModuleId: PERMISSION_MODULES.PROYECTOS_Y_TAREAS,
      items: [
        {
          label: 'Mi Bandeja de Trabajo',
          icon: 'pi pi-fw pi-inbox',
          routerLink: ['/proyectos/mis-tareas'],
          permissionId: PERMISSIONS_PROYECTOS_Y_TAREAS.VER_PROYECTOS_Y_TAREAS,
        },
        {
          label: 'Mis Tareas Sueltas',
          icon: 'pi pi-fw pi-check-square',
          routerLink: ['/proyectos/tareas-sueltas'],
          permissionId: PERMISSIONS_PROYECTOS_Y_TAREAS.VER_PROYECTOS_Y_TAREAS,
        },
      ],
    },
    {
      label: 'Planificación y Cronograma',
      icon: 'pi pi-calendar',
      moduleType: 'proyectos',
      permissionModuleId: PERMISSION_MODULES.PROYECTOS_Y_TAREAS,
      items: [
        {
          label: 'Calendario y Entregas',
          icon: 'pi pi-fw pi-calendar-clock',
          routerLink: ['/proyectos/calendario'],
          permissionId: PERMISSIONS_PROYECTOS_Y_TAREAS.VER_PROYECTOS_Y_TAREAS,
        },
      ],
    },

    // ========================================
    // SOPORTE (cliente final + agente)
    // ========================================
    {
      label: 'Mis Tickets',
      icon: 'pi pi-ticket',
      moduleType: 'soporte',
      items: [
        {
          label: 'Mis Tickets',
          icon: 'pi pi-fw pi-list',
          routerLink: ['/soporte/tickets'],
          permissionId: PERMISSIONS_SOPORTE.VER_TICKETS,
        },
        {
          label: 'Roadmap',
          icon: 'pi pi-fw pi-map',
          routerLink: ['/soporte/roadmap'],
          permissionId: PERMISSIONS_SOPORTE.VER_TICKETS,
        },
      ],
    },
    {
      label: 'Gestión de Soporte',
      icon: 'pi pi-cog',
      moduleType: 'admin',
      visible: isAdmin,
      permissionModuleId: PERMISSION_MODULES.SOPORTE,
      items: [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/soporte-admin/dashboard'],
          permissionId: PERMISSIONS_SOPORTE.VER_METRICAS,
        },
        {
          label: 'Tickets',
          icon: 'pi pi-fw pi-inbox',
          routerLink: ['/soporte-admin/tickets'],
          permissionId: PERMISSIONS_SOPORTE.GESTIONAR_TICKETS,
        },
        {
          label: 'Configuración SLA',
          icon: 'pi pi-fw pi-clock',
          routerLink: ['/soporte-admin/config'],
          permissionId: PERMISSIONS_SOPORTE.EDITAR_CONFIGURACION,
        },
        {
          label: 'Plantillas de Respuesta',
          icon: 'pi pi-fw pi-comment',
          routerLink: ['/soporte-admin/plantillas'],
          permissionId: PERMISSIONS_SOPORTE.EDITAR_CONFIGURACION,
        },
      ],
    },

    // ========================================
    // GESTIÓN EMPRESARIAL (Solo Admin)
    // ========================================
    {
      label: 'Gestión Empresarial',
      icon: 'pi pi-briefcase',
      moduleType: 'admin',
      visible: isAdmin,
      items: [
        {
          label: 'Tenants',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/admin'],
        },
        // Tres entradas, tres destinos REALES, tres cadencias distintas: el alta y la consulta son
        // continuas, la bandeja es diaria y el cierre es mensual.
        //
        // Antes había seis entradas que apuntaban todas a `/admin/resellers` cambiando un
        // `?focus=`, porque cinco de ellas no eran secciones sino PESTAÑAS de un reseller que aún
        // no se había elegido. Moverse por el menú no cambiaba de sitio: solo cambiaba el texto de
        // un recuadro de ayuda. Esas cinco desaparecen —a sus pestañas se llega desde la ficha— y
        // en su lugar entran las dos vistas que sí son globales y que hasta ahora no estaban en
        // ningún menú: la bandeja de solicitudes (antes, una pestaña por reseller) y las
        // liquidaciones (antes, un botón escondido en la barra del listado).
        {
          label: 'Canal de distribución',
          icon: 'pi pi-fw pi-users',
          items: [
            {
              label: 'Distribuidores',
              icon: 'pi pi-fw pi-id-card',
              routerLink: ['/admin/resellers'],
            },
            {
              label: 'Solicitudes',
              icon: 'pi pi-fw pi-inbox',
              routerLink: ['/admin/resellers/solicitudes'],
            },
            {
              label: 'Liquidaciones',
              icon: 'pi pi-fw pi-calculator',
              routerLink: ['/admin/resellers/liquidaciones'],
            },
            {
              label: 'Zonas de cobertura',
              icon: 'pi pi-fw pi-map',
              routerLink: ['/admin/resellers/cobertura'],
            },
          ],
        },

        {
          label: 'Planes',
          icon: 'pi pi-fw pi-credit-card',
          routerLink: ['/admin/plans'],
        },
        {
          label: 'Módulos',
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/admin/modules'],
        },
        {
          label: 'Permisos',
          icon: 'pi pi-fw pi-key',
          routerLink: ['/admin/permissions'],
        },
        { separator: true },
        {
          label: 'Mensajes de Contacto',
          icon: 'pi pi-fw pi-envelope',
          routerLink: ['/admin/contact-messages'],
        },
      ],
    },
  ];
}
